export interface FrameCopy {
  readonly title?: string;
  readonly description?: string;
}

interface DynamicTitle {
  readonly filterKey: string;
  readonly prefix: string;
  readonly optionSet: readonly {
    readonly value: string;
    readonly label: string;
  }[];
}

/**
 * Title and description for a status frame.
 *
 * A dynamic title reads the selected option of one form-state key. It does
 * not belong in a data-source cache key: changing the selection changes the
 * title only.
 */
export function resolveFrameCopy(
  props: {
    readonly title?: unknown;
    readonly description?: unknown;
    readonly dynamicTitle?: unknown;
  },
  formState: object,
): FrameCopy {
  const state = formState as Readonly<Record<string, unknown>>;
  const dynamic = readDynamicTitle(props.dynamicTitle);
  const selected = dynamic
    ? dynamic.optionSet.find(
        (option) => option.value === selectedValue(state[dynamic.filterKey]),
      )
    : undefined;

  const title =
    selected && dynamic
      ? joinPrefix(dynamic.prefix, selected.label)
      : typeof props.title === 'string' && props.title !== ''
        ? props.title
        : undefined;
  const description =
    typeof props.description === 'string' && props.description !== ''
      ? props.description
      : undefined;

  return {
    ...(title !== undefined ? { title } : {}),
    ...(description !== undefined ? { description } : {}),
  };
}

function joinPrefix(prefix: string, label: string): string {
  return prefix === '' ? label : `${prefix} ${label}`;
}

function selectedValue(raw: unknown): string | undefined {
  if (typeof raw === 'string' && raw.length > 0) return raw;
  if (!Array.isArray(raw)) return undefined;
  return raw.find(
    (entry): entry is string => typeof entry === 'string' && entry.length > 0,
  );
}

function readDynamicTitle(raw: unknown): DynamicTitle | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const record = raw as Record<string, unknown>;
  if (typeof record.filterKey !== 'string' || record.filterKey.length === 0) {
    return undefined;
  }
  if (typeof record.prefix !== 'string') return undefined;
  if (!Array.isArray(record.optionSet)) return undefined;

  const optionSet: { value: string; label: string }[] = [];
  for (const entry of record.optionSet) {
    if (typeof entry !== 'object' || entry === null) continue;
    const option = entry as Record<string, unknown>;
    if (typeof option.value !== 'string' || typeof option.label !== 'string') {
      continue;
    }
    optionSet.push({ value: option.value, label: option.label });
  }

  return { filterKey: record.filterKey, prefix: record.prefix, optionSet };
}
